import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  X,
  FileText,
  Download,
  User,
  Building2,
  Calendar,
  MapPin,
  Briefcase,
  Plane,
  Hotel,
  DollarSign,
  CheckCircle,
  XCircle,
  Clock,
  Paperclip,
  ShieldCheck,
  AlertCircle,
  ExternalLink,
  ChevronRight,
  Eye,
  Image as ImageIcon,
  Receipt,
  Link2,
  FileSpreadsheet,
} from 'lucide-react';
import { toast } from 'sonner';
import AttachmentPreviewModal from '../../components/travel/AttachmentPreviewModal';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TravelRequestDetailsModal = ({ isOpen, onClose, requestId, moduleType = 'outstation', onActionCompleted, currentUser }) => {
  const [loading, setLoading] = useState(true);
  const [requestDoc, setRequestDoc] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [approvedAdvanceAmount, setApprovedAdvanceAmount] = useState('');
  const [actionProcessing, setActionProcessing] = useState(null);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const fetchDetails = useCallback(async () => {
    if (!requestId) return;
    try {
      setLoading(true);
      if (moduleType === 'local') {
        const { data } = await axios.get(`${API_URL}/ess/local-travel/reports/${requestId}`, { withCredentials: true });
        const doc = data.report || data;
        setRequestDoc(doc);
      } else {
        const { data } = await axios.get(`${API_URL}/travel-desk/requests/${requestId}`, { withCredentials: true });
        const doc = data.request || data;
        setRequestDoc(doc);
        const reqAdv = doc.requested_advance_amount || doc.advance_details?.requested_advance_amount || doc.advance_details?.total_advance || doc.advance_amount || 0;
        setApprovedAdvanceAmount(doc.approved_advance_amount ?? doc.advance_details?.approved_advance_amount ?? reqAdv);
      }
    } catch (err) {
      console.error('Failed to load request details:', err);
      toast.error(err.response?.data?.detail || 'Failed to load request details');
    } finally {
      setLoading(false);
    }
  }, [requestId, moduleType]);

  useEffect(() => {
    if (isOpen && requestId) {
      fetchDetails();
      setRemarks('');
    }
  }, [isOpen, requestId, fetchDetails]);

  if (!isOpen) return null;

  const isLocal = moduleType === 'local';

  // Handle PDF Download
  const handleDownloadPdf = async () => {
    try {
      toast.info('Generating PDF document...');
      const endpoint = isLocal
        ? `${API_URL}/ess/local-travel/reports/${requestId}/pdf`
        : `${API_URL}/travel-desk/requests/${requestId}/pdf`;

      const res = await axios.get(endpoint, {
        responseType: 'blob',
        withCredentials: true,
      });

      const refNo = requestDoc?.request_number || requestDoc?.report_number || requestId;
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${isLocal ? 'Local_Conveyance' : 'Travel_Request'}_${refNo}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded successfully!');
    } catch (err) {
      toast.error('Failed to download PDF');
    }
  };

  // Process Approval Action inside modal
  const handleAction = async (action) => {
    if ((action === 'reject' || action === 'send_back' || action === 'send-back') && !remarks.trim()) {
      toast.error('Approver remarks are required when rejecting or sending back.');
      return;
    }

    try {
      setActionProcessing(action);
      if (isLocal) {
        const actClean = action === 'send_back' ? 'send-back' : action;
        const endpoint = `${API_URL}/ess/local-travel/finance/${requestId}/${actClean}`;
        const payload = action === 'reject'
          ? { rejection_reason: remarks, remarks }
          : { approval_remarks: remarks, remarks };
        await axios.post(endpoint, payload, { withCredentials: true });
      } else {
        const payload = { action, comments: remarks };
        if (action === 'approve' && (requestDoc?.advance_requested || (requestDoc?.advance_details?.total_advance > 0))) {
          const parsedVal = parseFloat(approvedAdvanceAmount);
          if (approvedAdvanceAmount === '' || isNaN(parsedVal) || parsedVal < 0) {
            toast.error('Please enter a valid non-negative approved advance amount.');
            setActionProcessing(null);
            return;
          }
          payload.approved_advance_amount = parsedVal;
        }

        await axios.post(
          `${API_URL}/travel-desk/requests/${requestId}/action`,
          payload,
          { withCredentials: true }
        );
      }
      toast.success(`Request ${action.replace('_', ' ')}d successfully!`);
      if (onActionCompleted) onActionCompleted();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action} request`);
    } finally {
      setActionProcessing(null);
    }
  };

  const reqNum = requestDoc?.request_number || requestDoc?.report_number || requestDoc?.report_id || requestId;
  const status = (requestDoc?.approval_status || requestDoc?.status || 'submitted').toUpperCase().replace(/_/g, ' ');
  const approvals = requestDoc?.approvals || [];
  const curIdx = requestDoc?.current_level_index;
  const isPendingForUser = curIdx !== null && curIdx !== undefined && curIdx < approvals.length && approvals[curIdx]?.status === 'pending';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        
        {/* Modal Top Header */}
        <div className="bg-slate-900 text-white p-5 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 text-blue-400 rounded-xl border border-blue-500/30">
              <FileText className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] bg-blue-500/30 text-blue-200 font-bold px-2 py-0.5 rounded border border-blue-400/30 uppercase">
                  {isLocal ? 'Local Conveyance' : 'Travel / Outstation'}
                </span>
                <span className="font-mono text-xs font-bold text-blue-300">{reqNum}</span>
              </div>
              <h3 className="text-lg font-bold text-white mt-0.5">
                {requestDoc?.beneficiary_employee_name || requestDoc?.employee_name || 'Travel Request'} Details
              </h3>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleDownloadPdf}
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all shadow-sm"
            >
              <Download className="w-4 h-4" /> Download PDF
            </button>
            <button
              onClick={onClose}
              className="p-1.5 text-slate-400 hover:text-white hover:bg-white/10 rounded-xl transition-all"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Modal Body Content */}
        {loading ? (
          <div className="p-12 text-center text-slate-500 font-medium">Loading full travel request details...</div>
        ) : !requestDoc ? (
          <div className="p-12 text-center text-slate-400 font-semibold italic">Request details not available.</div>
        ) : (
          <div className="p-6 overflow-y-auto space-y-6 flex-1 text-slate-800 text-xs">
            
            {/* Status Banner */}
            <div className="bg-slate-100 p-4 rounded-xl border border-slate-200 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3 items-center">
              <div>
                <span className="text-[11px] text-slate-400 font-bold block uppercase tracking-wider">Overall Requisition Status</span>
                <span className="inline-block mt-0.5 px-3 py-1 bg-amber-100 text-amber-900 border border-amber-300 font-extrabold rounded-full text-xs uppercase">
                  {status}
                </span>
              </div>
              <div>
                <span className="text-[11px] text-slate-400 font-bold block uppercase tracking-wider">Finance Payment Status</span>
                <span className={`inline-block mt-0.5 px-2.5 py-1 font-extrabold rounded-full text-[11px] uppercase border ${
                  (requestDoc.advance_status === 'paid' || requestDoc.finance_payment_status === 'paid')
                    ? 'bg-emerald-100 text-emerald-900 border-emerald-300'
                    : (status === 'APPROVED' && (requestDoc.advance_requested || requestDoc.advance_details?.total_advance > 0))
                    ? 'bg-indigo-100 text-indigo-950 border-indigo-300'
                    : 'bg-slate-200 text-slate-700 border-slate-300'
                }`}>
                  {requestDoc.advance_status === 'paid'
                    ? 'PAID'
                    : (status === 'APPROVED' && (requestDoc.advance_requested || (requestDoc.advance_details?.total_advance > 0)))
                    ? 'APPROVED - PAYMENT PENDING'
                    : (requestDoc.finance_payment_status?.toUpperCase().replace(/_/g, ' ') || 'N/A')}
                </span>
              </div>
              <div>
                <span className="text-[11px] text-slate-400 font-bold block uppercase tracking-wider">Approved By</span>
                <span className="font-bold text-slate-900 font-mono text-xs block truncate" title={requestDoc.approved_by_name || requestDoc.approved_by || 'Pending Review'}>
                  {requestDoc.approved_by_name || requestDoc.approved_by || 'Pending Review'}
                </span>
              </div>
              <div>
                <span className="text-[11px] text-slate-400 font-bold block uppercase tracking-wider">Approval Date</span>
                <span className="font-bold text-slate-900 font-mono text-xs block">
                  {requestDoc.approved_at ? requestDoc.approved_at.replace('T', ' ').slice(0, 16) : (requestDoc.approval_date || 'N/A')}
                </span>
              </div>
              <div>
                <span className="text-[11px] text-slate-400 font-bold block uppercase tracking-wider">Workflow Chain</span>
                <div className="flex items-center gap-1.5 text-slate-700 font-bold text-xs mt-0.5">
                  <ShieldCheck className="w-4 h-4 text-blue-600 shrink-0" />
                  <span className="truncate" title={requestDoc.approval_workflow_name || 'Standard Approval Chain'}>
                    {requestDoc.approval_workflow_name || 'Standard Approval Chain'}
                  </span>
                </div>
              </div>
            </div>

            {/* Section 1: Requester & Beneficiary Details */}
            <div className="bg-white p-4 rounded-xl border border-slate-200 space-y-3">
              <h4 className="font-extrabold text-blue-900 text-sm flex items-center gap-1.5 border-b border-slate-100 pb-2">
                <User className="w-4 h-4 text-blue-600" /> 1. Requester &amp; Travelling Employee Details
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <span className="text-[11px] text-slate-400 font-medium block">Travelling Employee (Beneficiary):</span>
                  <span className="font-bold text-slate-900">
                    {requestDoc.beneficiary_employee_name || requestDoc.employee_name || 'N/A'} ({requestDoc.beneficiary_employee_code || requestDoc.employee_code || 'EMP'})
                  </span>
                </div>
                <div>
                  <span className="text-[11px] text-slate-400 font-medium block">Requested By:</span>
                  <span className="font-bold text-slate-900">
                    {requestDoc.requester_name || requestDoc.created_by_name || requestDoc.employee_name || 'N/A'} ({requestDoc.requester_code || requestDoc.employee_code || 'N/A'})
                  </span>
                </div>
                <div>
                  <span className="text-[11px] text-slate-400 font-medium block">Department:</span>
                  <span className="font-bold text-slate-900">{requestDoc.department_name || requestDoc.department || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-[11px] text-slate-400 font-medium block">Designation / Grade:</span>
                  <span className="font-bold text-slate-900">
                    {requestDoc.designation || requestDoc.designation_name || 'Staff'} ({requestDoc.grade || 'Grade N/A'})
                  </span>
                </div>
              </div>
            </div>

            {/* Section 2: Purpose & Travel Information */}
            <div className="bg-white p-4 rounded-xl border border-slate-200 space-y-3">
              <h4 className="font-extrabold text-blue-900 text-sm flex items-center gap-1.5 border-b border-slate-100 pb-2">
                <Briefcase className="w-4 h-4 text-blue-600" /> 2. Purpose &amp; Travel Classification
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <span className="text-[11px] text-slate-400 font-medium block">Purpose of Visit:</span>
                  <span className="font-bold text-slate-900">{requestDoc.purpose || requestDoc.travel_purpose || 'Business Visit'}</span>
                </div>
                <div>
                  <span className="text-[11px] text-slate-400 font-medium block">Project / Cost Centre:</span>
                  <span className="font-bold text-slate-900">{requestDoc.project_name || requestDoc.project || 'Internal'}</span>
                </div>
                <div>
                  <span className="text-[11px] text-slate-400 font-medium block">Travel Category:</span>
                  <span className="font-bold text-slate-900">{requestDoc.travel_category || 'Domestic'}</span>
                </div>
                <div>
                  <span className="text-[11px] text-slate-400 font-medium block">Primary Travel Mode:</span>
                  <span className="font-bold text-slate-900 capitalize">{requestDoc.travel_type || requestDoc.travel_mode || 'Flight'}</span>
                </div>
              </div>
            </div>

            {/* Section 3: Journey Details Manifest Table */}
            <div className="bg-white p-4 rounded-xl border border-slate-200 space-y-3">
              <h4 className="font-extrabold text-blue-900 text-sm flex items-center gap-1.5 border-b border-slate-100 pb-2">
                <MapPin className="w-4 h-4 text-blue-600" /> 3. Complete Journey Segments Manifest
              </h4>
              {isLocal ? (
                <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
                  <p className="font-bold text-slate-800">
                    Route: <span className="text-blue-900">{requestDoc.connected_route || `${requestDoc.from_location || 'Origin'} → ${requestDoc.to_location || 'Destination'}`}</span>
                  </p>
                  <p className="text-slate-600 font-medium mt-1">Total Distance: <b>{requestDoc.total_km || 0} KM</b></p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse border border-slate-200 rounded-xl overflow-hidden">
                    <thead>
                      <tr className="bg-slate-100 text-slate-700 font-bold border-b border-slate-200">
                        <th className="p-2.5">#</th>
                        <th className="p-2.5">From City</th>
                        <th className="p-2.5">To City</th>
                        <th className="p-2.5">Departure Date &amp; Time</th>
                        <th className="p-2.5">Return Date &amp; Time</th>
                        <th className="p-2.5">Mode &amp; Preference</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {(requestDoc.journey_details || [
                        {
                          from_city: requestDoc.origin_city || 'Origin',
                          to_city: requestDoc.destination_city || 'Destination',
                          departure_date: requestDoc.start_date,
                          return_date: requestDoc.end_date,
                          travel_mode: requestDoc.travel_type || 'flight',
                          booking_preference: 'Economy',
                        }
                      ]).map((leg, idx) => (
                        <tr key={idx} className="hover:bg-slate-50 font-medium">
                          <td className="p-2.5 font-bold">{idx + 1}</td>
                          <td className="p-2.5">{leg.from_city || leg.from || '—'}</td>
                          <td className="p-2.5">{leg.to_city || leg.to || '—'}</td>
                          <td className="p-2.5">{leg.departure_date || '—'} {leg.departure_time || ''}</td>
                          <td className="p-2.5">{leg.return_date || '—'} {leg.return_time || ''}</td>
                          <td className="p-2.5 capitalize">{leg.travel_mode || requestDoc.travel_type} ({leg.booking_preference || leg.class || 'Economy'})</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Section 4: Passenger Manifest */}
            {requestDoc.passengers && requestDoc.passengers.length > 0 && (
              <div className="bg-white p-4 rounded-xl border border-slate-200 space-y-3">
                <h4 className="font-extrabold text-blue-900 text-sm flex items-center gap-1.5 border-b border-slate-100 pb-2">
                  <User className="w-4 h-4 text-blue-600" /> 4. Passenger Manifest ({requestDoc.passengers.length})
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse border border-slate-200 rounded-xl overflow-hidden">
                    <thead>
                      <tr className="bg-slate-100 text-slate-700 font-bold border-b border-slate-200">
                        <th className="p-2.5">#</th>
                        <th className="p-2.5">Passenger Name</th>
                        <th className="p-2.5">Employee ID / Code</th>
                        <th className="p-2.5">Type / Age / Gender</th>
                        <th className="p-2.5">ID Document</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 font-medium">
                      {requestDoc.passengers.map((p, idx) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="p-2.5 font-bold">{idx + 1}</td>
                          <td className="p-2.5 font-bold">{p.name || p.passenger_name}</td>
                          <td className="p-2.5">{p.employee_code || p.employee_id || '—'}</td>
                          <td className="p-2.5">{p.type || 'Employee'} | Age: {p.age || '—'} | {p.gender || '—'}</td>
                          <td className="p-2.5">{p.id_type || 'Govt ID'}: {p.id_number || 'On File'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Section 4B: Ticket Booking Information */}
            {(() => {
              const tb = requestDoc.ticket_booking_info || requestDoc.booking_record || {};
              const isBooked = requestDoc.ticket_booking_status === 'booked' || requestDoc.booking_status === 'confirmed' || !!requestDoc.booking_record || (tb.booking_status === 'booked');
              const isNotBooked = requestDoc.ticket_booking_status === 'not_booked' || requestDoc.booking_status === 'not_booked' || (tb.booking_status === 'not_booked') || !!requestDoc.ticket_not_booked_reason;

              if (!isBooked && !isNotBooked && !['approved', 'booked'].includes(requestDoc.status)) {
                return null;
              }

              return (
                <div className="bg-white p-4 rounded-xl border border-slate-200 space-y-3" data-testid="section-ticket-booking-info">
                  <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                    <h4 className="font-extrabold text-slate-900 text-sm flex items-center gap-1.5">
                      <Plane className="w-4 h-4 text-indigo-600" /> Ticket Booking Information
                    </h4>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-black uppercase tracking-wide border ${
                        isBooked
                          ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
                          : isNotBooked
                          ? 'bg-amber-50 text-amber-900 border-amber-300'
                          : 'bg-slate-100 text-slate-700 border-slate-200'
                      }`}
                    >
                      {isBooked ? '✓ Ticket Booked' : isNotBooked ? '⚠ Ticket Not Booked' : 'Pending Booking'}
                    </span>
                  </div>

                  {isNotBooked ? (
                    <div className="space-y-2 p-3 bg-amber-50/70 border border-amber-200 rounded-xl">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                        <div className="space-y-1 flex-1 text-xs">
                          <span className="font-bold text-amber-950 uppercase text-[10px] tracking-wide block">
                            Reason for Not Booking Ticket:
                          </span>
                          <p className="text-amber-900 font-medium whitespace-pre-wrap">
                            {requestDoc.ticket_not_booked_reason || tb.not_booked_reason || 'Ticket was not booked by booking controller / travel desk.'}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2 pt-2 border-t border-amber-200/60 text-[11px] text-amber-800">
                        <div>
                          <span className="font-bold text-amber-950">Recorded Date:</span>{' '}
                          {requestDoc.ticket_not_booked_date || tb.booking_date || requestDoc.updated_at?.slice(0, 10) || '—'}
                        </div>
                        <div>
                          <span className="font-bold text-amber-950">Recorded By:</span>{' '}
                          {requestDoc.ticket_not_booked_by || tb.booked_by || 'Booking Controller / Travel Desk'}
                        </div>
                      </div>

                      <div className="text-[11px] text-slate-500 italic pt-1">
                        * The employee or booking controller can enter the ticket details later if required.
                      </div>
                    </div>
                  ) : isBooked ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 text-xs">
                        <div className="p-2.5 bg-slate-50 rounded-xl border border-slate-200">
                          <span className="text-[10px] text-slate-400 font-bold uppercase block">PNR / Reference</span>
                          <span className="font-mono font-bold text-slate-900 text-xs">
                            {tb.pnr_reference || requestDoc.booking_record?.pnr_reference || '—'}
                          </span>
                        </div>
                        <div className="p-2.5 bg-slate-50 rounded-xl border border-slate-200">
                          <span className="text-[10px] text-slate-400 font-bold uppercase block">Carrier / Flight / Train</span>
                          <span className="font-bold text-slate-800 text-xs">
                            {tb.carrier_name_or_no || requestDoc.booking_record?.carrier_name_or_no || '—'}
                          </span>
                        </div>
                        <div className="p-2.5 bg-slate-50 rounded-xl border border-slate-200">
                          <span className="text-[10px] text-slate-400 font-bold uppercase block">Vendor / Carrier</span>
                          <span className="font-bold text-slate-800 text-xs">
                            {tb.vendor_name || requestDoc.booking_record?.vendor_name || 'Preferred Vendor'}
                          </span>
                        </div>
                        <div className="p-2.5 bg-slate-50 rounded-xl border border-slate-200">
                          <span className="text-[10px] text-slate-400 font-bold uppercase block">Payment Method</span>
                          <span className="font-bold text-indigo-700 text-xs">
                            {tb.credit_card_masked || requestDoc.booking_record?.credit_card_masked || 'Corporate Account'}
                          </span>
                        </div>
                      </div>

                      {/* Fare Breakdown */}
                      <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-1.5 text-xs">
                        <span className="font-bold text-slate-700 text-[11px] uppercase tracking-wide block">
                          Fare Breakdown
                        </span>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          <div>
                            <span className="text-[10px] text-slate-400 block">Base Fare</span>
                            <span className="font-mono font-bold text-slate-800">
                              ₹ {Number(tb.base_fare ?? requestDoc.booking_record?.base_fare ?? 0).toLocaleString('en-IN')}
                            </span>
                          </div>
                          <div>
                            <span className="text-[10px] text-slate-400 block">Taxes &amp; GST</span>
                            <span className="font-mono font-bold text-slate-800">
                              ₹ {Number(tb.taxes ?? requestDoc.booking_record?.taxes ?? 0).toLocaleString('en-IN')}
                            </span>
                          </div>
                          <div>
                            <span className="text-[10px] text-slate-400 block">Convenience Fees</span>
                            <span className="font-mono font-bold text-slate-800">
                              ₹ {Number(tb.convenience_fee ?? requestDoc.booking_record?.convenience_fee ?? 0).toLocaleString('en-IN')}
                            </span>
                          </div>
                          <div>
                            <span className="text-[10px] text-slate-400 block">Total Fare</span>
                            <span className="font-mono font-black text-emerald-700 text-sm">
                              ₹ {Number(tb.total_amount ?? requestDoc.booking_record?.total_amount ?? 0).toLocaleString('en-IN')}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-[11px] text-slate-500 pt-1">
                        <div>
                          <span className="font-bold text-slate-700">Booking Date:</span>{' '}
                          {tb.booking_date || requestDoc.booking_record?.booking_date || '—'}
                        </div>
                        <div>
                          <span className="font-bold text-slate-700">Booked By:</span>{' '}
                          {tb.booked_by || requestDoc.booking_record?.booked_by || 'Travel Desk Executive'}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-xs text-slate-500 italic p-2 bg-slate-50 rounded-lg">
                      Tickets pending booking by assigned Booking Controller or Travel Desk Executive.
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Section 4C: Dedicated Ticket Booking Requests */}
            {requestDoc.ticket_booking_requests && requestDoc.ticket_booking_requests.length > 0 && (
              <div className="bg-white p-4 rounded-xl border border-slate-200 space-y-3">
                <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                  <h4 className="font-extrabold text-slate-900 text-sm flex items-center gap-1.5">
                    <Plane className="w-4 h-4 text-blue-600" /> Dedicated Ticket Booking Requests ({requestDoc.ticket_booking_requests.length})
                  </h4>
                </div>
                <div className="space-y-2">
                  {requestDoc.ticket_booking_requests.map((tbr, idx) => (
                    <div key={tbr.ticket_request_id || idx} className="p-3 bg-slate-50 rounded-xl border border-slate-200 text-xs space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="font-mono font-bold text-blue-700">{tbr.ticket_request_id}</span>
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-blue-100 text-blue-800">
                          {tbr.status_label || tbr.status}
                        </span>
                      </div>
                      <div className="font-bold text-slate-900">
                        {tbr.from_location} → {tbr.to_location} ({tbr.departure_date} {tbr.return_date ? `to ${tbr.return_date}` : ''})
                      </div>
                      <div className="text-slate-600">
                        {tbr.travel_mode} • {tbr.booking_type} • Est. ₹ {(Number(tbr.estimated_ticket_amount) || 0).toLocaleString('en-IN')}
                      </div>
                      {tbr.booking_details && (
                        <div className="p-2 bg-emerald-50 rounded-lg text-emerald-900 font-mono text-[11px]">
                          PNR: {tbr.booking_details.pnr} | Carrier: {tbr.booking_details.carrier} | Fare: ₹ {(tbr.booking_details.booking_amount || 0).toLocaleString('en-IN')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Section 5: Advance Requisition Breakdown */}
            {(requestDoc.advance_requested || (requestDoc.advance_details?.total_advance > 0)) && (
              <div className="bg-amber-50/80 p-4 rounded-xl border border-amber-200 space-y-3">
                <h4 className="font-extrabold text-amber-950 text-sm flex items-center gap-1.5 border-b border-amber-200 pb-2">
                  <DollarSign className="w-4 h-4 text-amber-700" /> 5. Travel Advance Requisition Breakdown
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-amber-900 font-medium">
                  <div>Hotel: <b>₹ {(requestDoc.advance_details?.hotel_amount || 0).toLocaleString('en-IN')}</b></div>
                  <div>Food: <b>₹ {(requestDoc.advance_details?.food_amount || 0).toLocaleString('en-IN')}</b></div>
                  <div>Local Travel: <b>₹ {(requestDoc.advance_details?.local_travel_amount || 0).toLocaleString('en-IN')}</b></div>
                  <div>Misc: <b>₹ {(requestDoc.advance_details?.misc_amount || 0).toLocaleString('en-IN')}</b></div>
                </div>
                <div className="pt-2 border-t border-amber-200 flex justify-between items-center flex-wrap gap-2 font-bold text-amber-950">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-amber-900">Requested Advance:</span>
                    <span className="text-sm font-black font-mono bg-amber-200 px-3 py-1 rounded-lg">
                      ₹ {(requestDoc.requested_advance_amount || requestDoc.advance_details?.requested_advance_amount || requestDoc.advance_details?.total_advance || requestDoc.advance_amount || 0).toLocaleString('en-IN')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-emerald-900">Approved Advance:</span>
                    <span className="text-sm font-black font-mono bg-emerald-200 text-emerald-950 px-3 py-1 rounded-lg">
                      {requestDoc.approved_advance_amount !== null && requestDoc.approved_advance_amount !== undefined
                        ? `₹ ${Number(requestDoc.approved_advance_amount).toLocaleString('en-IN')}`
                        : (requestDoc.advance_details?.approved_advance_amount !== null && requestDoc.advance_details?.approved_advance_amount !== undefined
                            ? `₹ ${Number(requestDoc.advance_details.approved_advance_amount).toLocaleString('en-IN')}`
                            : 'Pending Approval')}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Section 5B: Reimbursement Claim Breakdown */}
            {(requestDoc.is_self_booking || requestDoc.claim_type === 'self_booking' || requestDoc.self_booking_details) && (() => {
              const sb = requestDoc.self_booking_details || {};
              const ticketAmt = sb.ticket_amount || 0;
              const hotelAmt = sb.hotel_amount || 0;
              const foodAmt = sb.food_amount || 0;
              const localAmt = sb.local_travel_amount || sb.conveyance_amount || 0;
              const miscAmt = sb.misc_amount || 0;
              const totalAmt = sb.total_claim_amount || requestDoc.total_claim_amount || requestDoc.estimated_budget || (ticketAmt + hotelAmt + foodAmt + localAmt + miscAmt);

              const allItems = [
                ...(sb.ticket_items || (ticketAmt > 0 ? [{ amount: ticketAmt, date: sb.ticket_date, vendor_ref: sb.ticket_vendor_ref, remarks: sb.ticket_remarks }] : [])).map(i => ({ ...i, category: 'Flight / Train / Ticket' })),
                ...(sb.hotel_items || (hotelAmt > 0 ? [{ amount: hotelAmt, date: sb.hotel_date, vendor_ref: sb.hotel_vendor_ref, remarks: sb.hotel_remarks }] : [])).map(i => ({ ...i, category: 'Hotel & Accommodation' })),
                ...(sb.food_items || (foodAmt > 0 ? [{ amount: foodAmt, date: sb.food_date, remarks: sb.food_remarks }] : [])).map(i => ({ ...i, category: 'Food & Meals' })),
                ...(sb.local_travel_items || (localAmt > 0 ? [{ amount: localAmt, date: sb.local_travel_date, remarks: sb.local_travel_remarks }] : [])).map(i => ({ ...i, category: 'Local Conveyance (Cab)' })),
                ...(sb.misc_items || (miscAmt > 0 ? [{ amount: miscAmt, date: sb.misc_date, remarks: sb.misc_remarks }] : [])).map(i => ({ ...i, category: 'Miscellaneous' }))
              ];

              return (
                <div className="bg-purple-50/70 p-4 rounded-xl border border-purple-200 space-y-3">
                  <div className="flex items-center justify-between border-b border-purple-200 pb-2">
                    <h4 className="font-extrabold text-purple-950 text-sm flex items-center gap-1.5">
                      <Receipt className="w-4 h-4 text-purple-700" /> Reimbursement Claim &amp; Actual Expense Breakdown
                    </h4>
                    <span className="font-mono font-black text-purple-900 text-xs bg-purple-200/80 px-2.5 py-1 rounded-lg">
                      Total Claim: ₹ {Number(totalAmt).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5 text-purple-950 font-medium text-xs">
                    <div className="bg-white/80 p-2 rounded-lg border border-purple-100">
                      <span className="text-[10px] text-purple-600 block">1. Ticket:</span>
                      <b className="font-mono">₹ {Number(ticketAmt).toLocaleString('en-IN')}</b>
                    </div>
                    <div className="bg-white/80 p-2 rounded-lg border border-purple-100">
                      <span className="text-[10px] text-purple-600 block">2. Hotel:</span>
                      <b className="font-mono">₹ {Number(hotelAmt).toLocaleString('en-IN')}</b>
                    </div>
                    <div className="bg-white/80 p-2 rounded-lg border border-purple-100">
                      <span className="text-[10px] text-purple-600 block">3. Food:</span>
                      <b className="font-mono">₹ {Number(foodAmt).toLocaleString('en-IN')}</b>
                    </div>
                    <div className="bg-white/80 p-2 rounded-lg border border-purple-100">
                      <span className="text-[10px] text-purple-600 block">4. Conveyance:</span>
                      <b className="font-mono">₹ {Number(localAmt).toLocaleString('en-IN')}</b>
                    </div>
                    <div className="bg-white/80 p-2 rounded-lg border border-purple-100">
                      <span className="text-[10px] text-purple-600 block">5. Misc:</span>
                      <b className="font-mono">₹ {Number(miscAmt).toLocaleString('en-IN')}</b>
                    </div>
                  </div>

                  {allItems.length > 0 && (
                    <div className="overflow-x-auto pt-1">
                      <table className="w-full text-left border-collapse border border-purple-200 rounded-lg overflow-hidden text-xs bg-white">
                        <thead>
                          <tr className="bg-purple-100/70 text-purple-900 font-bold border-b border-purple-200 text-[11px]">
                            <th className="p-2">#</th>
                            <th className="p-2">Category</th>
                            <th className="p-2">Date</th>
                            <th className="p-2">Reference / PNR</th>
                            <th className="p-2">Description / Remarks</th>
                            <th className="p-2 text-right">Amount (₹)</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-purple-100 font-medium">
                          {allItems.map((it, idx) => (
                            <tr key={idx} className="hover:bg-purple-50/40">
                              <td className="p-2 font-bold text-slate-500">{idx + 1}</td>
                              <td className="p-2 font-bold text-purple-950">{it.category}</td>
                              <td className="p-2 text-slate-600">{it.date || '—'}</td>
                              <td className="p-2 font-mono text-slate-700">{it.vendor_ref || '—'}</td>
                              <td className="p-2 text-slate-700">{it.remarks || '—'}</td>
                              <td className="p-2 font-mono font-bold text-slate-900 text-right">
                                ₹ {Number(it.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Section 5C: Post-Travel Expense Settlement */}
            {requestDoc.settlement && requestDoc.settlement.settlement_status !== 'not_submitted' && (() => {
              const stl = requestDoc.settlement;
              const driveLinks = stl.google_drive_links?.length > 0
                ? stl.google_drive_links
                : (stl.google_drive_link ? [stl.google_drive_link] : []);

              return (
                <div className="bg-emerald-50/70 p-4 rounded-xl border border-emerald-200 space-y-3" data-testid="modal-post-travel-settlement">
                  <div className="flex items-center justify-between border-b border-emerald-200 pb-2">
                    <h4 className="font-extrabold text-emerald-950 text-sm flex items-center gap-1.5">
                      <Receipt className="w-4 h-4 text-emerald-700" /> Post-Travel Expense Settlement
                    </h4>
                    <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase border ${
                      stl.settlement_status === 'settled'
                        ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                        : 'bg-amber-100 text-amber-800 border-amber-300'
                    }`}>
                      Status: {stl.settlement_status?.replace(/_/g, ' ')}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 text-xs">
                    <div className="p-2 bg-white rounded-lg border border-emerald-100">
                      <span className="text-[10px] text-slate-400 block font-bold uppercase">Hotel Incurred</span>
                      <span className="font-mono font-bold text-slate-800">₹ {(stl.actual_hotel || 0).toLocaleString('en-IN')}</span>
                    </div>
                    <div className="p-2 bg-white rounded-lg border border-emerald-100">
                      <span className="text-[10px] text-slate-400 block font-bold uppercase">Food Incurred</span>
                      <span className="font-mono font-bold text-slate-800">₹ {(stl.actual_food || 0).toLocaleString('en-IN')}</span>
                    </div>
                    <div className="p-2 bg-white rounded-lg border border-emerald-100">
                      <span className="text-[10px] text-slate-400 block font-bold uppercase">Local Travel</span>
                      <span className="font-mono font-bold text-slate-800">₹ {(stl.actual_local_travel || 0).toLocaleString('en-IN')}</span>
                    </div>
                    <div className="p-2 bg-white rounded-lg border border-emerald-100">
                      <span className="text-[10px] text-slate-400 block font-bold uppercase">Miscellaneous</span>
                      <span className="font-mono font-bold text-slate-800">₹ {(stl.actual_misc || 0).toLocaleString('en-IN')}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 p-3 bg-white rounded-xl border border-emerald-200 text-xs font-bold text-emerald-950">
                    <div>
                      <span className="text-[10px] text-slate-400 uppercase font-medium block">Advance Received</span>
                      <span className="font-mono">₹ {(stl.advance_received || 0).toLocaleString('en-IN')}</span>
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-400 uppercase font-medium block">Actual Incurred</span>
                      <span className="font-mono">₹ {(stl.actual_total || 0).toLocaleString('en-IN')}</span>
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-400 uppercase font-medium block">Approved Amount</span>
                      <span className="font-mono text-emerald-700">₹ {(stl.approved_total ?? stl.actual_total ?? 0).toLocaleString('en-IN')}</span>
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-400 uppercase font-medium block">Net Balance</span>
                      <span className="font-mono text-indigo-700">₹ {(stl.net_balance ?? stl.balance_amount ?? 0).toLocaleString('en-IN')}</span>
                    </div>
                  </div>

                  {stl.employee_notes && (
                    <div className="p-2.5 bg-white rounded-lg border border-emerald-100 text-xs">
                      <span className="text-[10px] font-bold text-slate-400 uppercase block mb-0.5">Employee Settlement Notes:</span>
                      <p className="text-slate-700 whitespace-pre-wrap">{stl.employee_notes}</p>
                    </div>
                  )}

                  {driveLinks.length > 0 && (
                    <div className="p-2.5 bg-white rounded-lg border border-indigo-200 space-y-1.5 text-xs">
                      <span className="text-[10px] font-bold text-indigo-900 uppercase block flex items-center gap-1">
                        <Link2 className="w-3.5 h-3.5 text-indigo-600" /> Google Drive Supporting Links ({driveLinks.length}):
                      </span>
                      {driveLinks.map((lnk, idx) => (
                        <div key={idx} className="flex items-center justify-between gap-2 p-1.5 bg-indigo-50/50 rounded border border-indigo-100 text-xs">
                          <span className="font-mono text-indigo-800 text-[11px] truncate flex-1">{lnk}</span>
                          <a
                            href={lnk}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-2 py-0.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded text-[10px] font-bold flex items-center gap-1 shrink-0"
                          >
                            <ExternalLink className="w-3 h-3" /> Open
                          </a>
                        </div>
                      ))}
                    </div>
                  )}

                  {stl.attachments?.length > 0 && (
                    <div className="p-2.5 bg-white rounded-lg border border-emerald-100 space-y-1.5 text-xs">
                      <span className="text-[10px] font-bold text-slate-500 uppercase block flex items-center gap-1">
                        <Paperclip className="w-3.5 h-3.5 text-blue-600" /> Settlement Bills &amp; Receipts ({stl.attachments.length}):
                      </span>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {stl.attachments.map((att, idx) => {
                          const isObj = att && typeof att === 'object';
                          const attName = isObj ? (att.name || att.filename || `Bill #${idx + 1}`) : String(att);
                          return (
                            <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 border border-slate-200 rounded-lg gap-2">
                              <span className="font-bold text-slate-800 truncate text-xs flex-1" title={attName}>
                                {attName}
                              </span>
                              <div className="flex items-center gap-1 shrink-0">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setPreviewAttachment(att);
                                    setIsPreviewOpen(true);
                                  }}
                                  className="px-2 py-0.5 bg-white hover:bg-blue-50 text-blue-600 border border-blue-200 rounded text-[11px] font-bold flex items-center gap-1"
                                >
                                  <Eye className="w-3 h-3" /> View
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Section 6: APPROVAL HISTORY */}
            <div className="bg-white p-4 rounded-xl border border-slate-200 space-y-3">
              <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                <h4 className="font-extrabold text-blue-900 text-sm flex items-center gap-1.5">
                  <Clock className="w-4 h-4 text-blue-600" /> APPROVAL HISTORY &amp; AUDIT TRAIL
                </h4>
                {(requestDoc.payment_processed_by || requestDoc.finance_audit?.payment_processed_by?.display) && (
                  <span className="text-[11px] font-bold text-indigo-950 bg-indigo-50 px-2.5 py-1 rounded-lg border border-indigo-200">
                    Payment Processed By: <b>{requestDoc.payment_processed_by || requestDoc.finance_audit?.payment_processed_by?.display}</b>
                  </span>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse border border-slate-200 rounded-xl overflow-hidden">
                  <thead>
                    <tr className="bg-slate-100 text-slate-700 font-bold border-b border-slate-200">
                      <th className="p-2.5">Level</th>
                      <th className="p-2.5">Approval Role / Stage</th>
                      <th className="p-2.5">Approved By (Employee Code)</th>
                      <th className="p-2.5">Status / Action</th>
                      <th className="p-2.5">Action Date &amp; Time</th>
                      <th className="p-2.5">Remarks / Reason</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 font-medium">
                    {((requestDoc.approval_history && requestDoc.approval_history.length > 0)
                      ? requestDoc.approval_history
                      : (approvals.length > 0 ? approvals : (requestDoc.approval_timeline || []))
                    ).map((lvl, idx) => {
                      const st = String(lvl.action || lvl.status || 'pending').toUpperCase();
                      const rawTime = lvl.approved_at || lvl.decided_at || lvl.timestamp;
                      const timeStr = rawTime ? String(rawTime).replace('T', ' ').slice(0, 19) : '—';
                      const approverDisp = lvl.approver_name || lvl.actor_name || lvl.approver_role || 'Approver';
                      return (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="p-2.5 font-bold text-slate-700">{lvl.level || idx + 1}</td>
                          <td className="p-2.5 font-bold text-slate-900">{lvl.role || lvl.level_name || lvl.step || `Level ${idx + 1}`}</td>
                          <td className="p-2.5 font-bold text-blue-950">{approverDisp}</td>
                          <td className="p-2.5">
                            <span className={`px-2.5 py-0.5 rounded text-[10px] font-extrabold uppercase border ${
                              st === 'APPROVED'
                                ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                                : st === 'PENDING'
                                ? 'bg-blue-100 text-blue-800 border-blue-300 font-extrabold'
                                : st === 'REJECTED'
                                ? 'bg-rose-100 text-rose-800 border-rose-300'
                                : st === 'SENT_BACK' || st === 'SENT BACK'
                                ? 'bg-amber-100 text-amber-800 border-amber-300'
                                : 'bg-slate-100 text-slate-600 border-slate-200'
                            }`}>
                              {st}
                            </span>
                          </td>
                          <td className="p-2.5 font-mono text-slate-600 text-[11px]">{timeStr}</td>
                          <td className="p-2.5 text-slate-600">{lvl.comments || lvl.remarks || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Section 7: Attachments */}
            {(() => {
              const modalAtts = [
                ...(requestDoc.attachments && requestDoc.attachments.length > 0 ? requestDoc.attachments : []),
                ...(requestDoc.self_booking_details?.all_attachments || []),
                ...(requestDoc.settlement?.attachments || []),
              ];

              if (!modalAtts || modalAtts.length === 0) return null;

              return (
                <div className="bg-white p-4 rounded-xl border border-slate-200 space-y-3">
                  <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                    <h4 className="font-extrabold text-blue-900 text-sm flex items-center gap-1.5">
                      <Paperclip className="w-4 h-4 text-blue-600" /> Uploaded Bills &amp; Receipts ({modalAtts.length})
                    </h4>
                    <span className="text-[10px] text-slate-400">Click View to preview or Download to save</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {modalAtts.map((att, idx) => {
                      const isObj = att && typeof att === 'object';
                      const attName = isObj ? (att.name || att.filename || `Receipt #${idx + 1}`) : (typeof att === 'string' && att.trim() ? att.split('?')[0].split('/').pop() || `Receipt #${idx + 1}` : `Receipt #${idx + 1}`);
                      const isPdf = attName.toLowerCase().endsWith('.pdf') || (isObj && att.type?.includes('pdf'));

                      return (
                        <div
                          key={idx}
                          className="p-2.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl text-xs flex items-center justify-between gap-2"
                        >
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <div className={`p-1.5 rounded-lg shrink-0 ${isPdf ? 'bg-rose-100 text-rose-600' : 'bg-blue-100 text-blue-600'}`}>
                              {isPdf ? <FileText className="w-4 h-4" /> : <ImageIcon className="w-4 h-4" />}
                            </div>
                            <div className="min-w-0 flex-1">
                              <span className="font-bold text-slate-800 truncate block" title={attName}>
                                {attName}
                              </span>
                              {isObj && att.size_formatted && (
                                <span className="text-[10px] text-slate-400 font-mono block">{att.size_formatted}</span>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              type="button"
                              onClick={() => {
                                setPreviewAttachment(att);
                                setIsPreviewOpen(true);
                              }}
                              className="px-2.5 py-1 bg-white hover:bg-blue-50 text-blue-700 border border-blue-200 rounded-lg text-xs font-bold flex items-center gap-1 shadow-xs transition-colors"
                              title="View Document"
                            >
                              <Eye className="w-3.5 h-3.5" /> View
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setPreviewAttachment(att);
                                setIsPreviewOpen(true);
                              }}
                              className="p-1 bg-white hover:bg-slate-100 text-slate-600 border border-slate-200 rounded-lg transition-colors"
                              title="Download Document"
                            >
                              <Download className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

          </div>
        )}

        {/* Modal Action Footer */}
        {isPendingForUser && (
          <div className="p-4 bg-slate-50 border-t border-slate-200 flex flex-col space-y-3 shrink-0">
            {!isLocal && (requestDoc?.advance_requested || (requestDoc?.advance_details?.total_advance > 0)) && (
              <div className="flex items-center justify-between gap-3 bg-amber-50 p-2.5 rounded-xl border border-amber-200">
                <span className="text-xs font-bold text-amber-950">
                  Approved Advance Amount (₹) *:
                </span>
                <div className="relative w-full max-w-xs">
                  <span className="absolute left-3 top-2 font-bold text-slate-500">₹</span>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    placeholder="Enter Approved Amount"
                    value={approvedAdvanceAmount}
                    onChange={(e) => setApprovedAdvanceAmount(e.target.value)}
                    className="w-full pl-7 pr-3 py-1.5 bg-white border border-amber-300 rounded-xl text-xs font-mono font-bold text-slate-900 outline-none focus:border-blue-500 shadow-sm"
                    disabled={!!actionProcessing}
                  />
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <input
                type="text"
                placeholder="Approver remarks / comments..."
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                className="h-10 px-3 bg-white border border-slate-300 rounded-xl text-xs w-full max-w-lg outline-none focus:border-blue-500"
                disabled={!!actionProcessing}
              />

              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => handleAction('send_back')}
                  disabled={!!actionProcessing}
                  className="px-4 py-2 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white font-bold text-xs rounded-xl transition-all"
                >
                  Return
                </button>
                <button
                  onClick={() => handleAction('reject')}
                  disabled={!!actionProcessing}
                  className="px-4 py-2 bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white font-bold text-xs rounded-xl transition-all"
                >
                  Reject
                </button>
                <button
                  onClick={() => handleAction('approve')}
                  disabled={!!actionProcessing}
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold text-xs rounded-xl shadow-md transition-all flex items-center gap-1.5"
                >
                  <CheckCircle className="w-4 h-4" /> Approve Request &rarr;
                </button>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Attachment Preview Modal */}
      <AttachmentPreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        attachment={previewAttachment}
        title="Bill &amp; Receipt Preview"
      />
    </div>
  );
};

export default TravelRequestDetailsModal;
